import React, { useState, useEffect, ChangeEvent, FormEvent } from 'react';
import styled from 'styled-components';
import api from '../../../utils/api';
import { apiSuccess } from '../../../utils/utils';
import { ActionButton, CancelButton } from '../common/AdminButtons';
import { Form, Input, ButtonGroup, Select, TextArea } from '../common/AdminForm';
import { ErrorMessage } from '../common/AdminMessages';
import Style  from '../../../models/Style';
import Jargon from '../../../models/Jargon';

const FormContainer = styled.div`
  padding: 1.5rem;
  background-color: #f9f9f9;
  border-radius: 8px;
`;

interface StyleFormProps {
  initialStyle?: Style;
  onSuccess: () => void;
  onCancel: () => void;
}

const StyleForm: React.FC<StyleFormProps> = ({ initialStyle, onSuccess, onCancel }) => {
  const [formData, setFormData] = useState<Style>(
    initialStyle
      ? { ...initialStyle }
      : {
          id: 0,
          name: '',
          averageSentencesPerParagraph: 0,
          averageWordsPerSentence:0,
          exclamationPointFrequency: 0.1,
          styleJargonId: 1,
          description: '',
          sequence:0
        }
  );
  const [jargons, setJargons] = useState<Jargon[]>([]);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    api.get('/halo/jargons/')
      .then(res => setJargons(res.data))
      .catch(() => setErrorMessage('Failed to load jargons for dropdown.'));
  }, []);

  const handleInputChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
  
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSelectChange = (e: ChangeEvent<HTMLSelectElement>) => {
    setFormData(prev => ({
      ...prev,
      styleJargonId: parseInt(e.target.value),
    }));
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
  
    const payload: Style = {
      ...formData,
      averageSentencesPerParagraph: parseFloat(formData.averageSentencesPerParagraph as any),
      exclamationPointFrequency: parseFloat(formData.exclamationPointFrequency as any),
      sequence: parseFloat(formData.sequence as any),
      styleJargonId: parseInt(formData.styleJargonId as any)
    };
  
    const response = initialStyle
      ? await api.put(`/halo/styles/${initialStyle.id}`, payload)
      : await api.post(`/halo/styles`, payload);
  
    if (apiSuccess(response)) {
      onSuccess();
    } else {
      setErrorMessage(`HTTP ${response.status}: ${response.statusText}`);
    }
  };

  return (
    <FormContainer>
      {errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}
      <Form onSubmit={handleSubmit}>
        <label>Name</label>
        <Input name="name" value={formData.name} onChange={handleInputChange} required />

        <label>Avg. Sentences per Paragraph</label>
        <Input
          type="number"
          name="averageSentencesPerParagraph"
          value={formData.averageSentencesPerParagraph}
          onChange={handleInputChange}
          step="1"
        />

      <label>Avg. Words per Sentence</label>
        <Input
          type="number"
          name="averageWordsPerSentence"
          value={formData.averageWordsPerSentence}
          onChange={handleInputChange}
          step="1"
        />

        <label>Exclamation Point Frequency</label>
        <Input
          type="text"
          name="exclamationPointFrequency"
          value={formData.exclamationPointFrequency}
          onChange={handleInputChange}
        />

        <label>Sequence</label>
        <Input
          type="number"
          name="sequence"
          value={formData.sequence}
          onChange={handleInputChange}
          step="0.01"
        />

        <label>Style Jargon</label>
        <Select name="styleJargonId" value={formData.styleJargonId} onChange={handleSelectChange}>
            <option value="0">None</option>
          {jargons.map(j => (
            <option key={j.id} value={j.id}>{j.name}</option>
          ))}
        </Select> 

        <label>Description (HTML allowed)</label>
        <TextArea
          name="description"
          value={formData.description}
          onChange={handleInputChange}
          rows={4}
        />

        <ButtonGroup>
          <ActionButton type="submit">{initialStyle ? 'Update Style' : 'Add Style'}</ActionButton>
          <CancelButton type="button" onClick={onCancel}>Cancel</CancelButton>
        </ButtonGroup>
      </Form>
    </FormContainer>
  );
};

export default StyleForm;
